import React, { useCallback, useMemo, useState } from "react";
import {
    Eye,
    EyeOff,
    FolderPlus,
    GripVertical,
    Pencil,
    RotateCcw,
    Settings2,
    Trash2,
} from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    isMenuPathVisible,
    MENU_ALWAYS_VISIBLE,
    setMenuPathVisible,
    showAllMenuPaths,
} from "@/lib/sidebarMenuPrefs";
import {
    addCustomMenuGroup,
    moveMenuItem,
    removeCustomMenuGroup,
    renameMenuGroup,
    reorderMenuGroups,
    resetMenuLayout,
} from "@/lib/sidebarMenuLayout";

const DND_PATH = "application/x-mbs-menu-path";
const DND_GROUP = "application/x-mbs-menu-group";

export default function SidebarMenuCustomizer({
    open,
    onOpenChange,
    layout,
    onLayoutChange,
    catalogGroups,
    navCatalog,
    hiddenPaths,
    onHiddenChange,
}) {
    const [newGroupName, setNewGroupName] = useState("");
    const [renameId, setRenameId] = useState(null);
    const [renameValue, setRenameValue] = useState("");
    const [dragOver, setDragOver] = useState(null);

    const customizerGroups = useMemo(() => {
        return (layout?.groups || []).map((g) => ({
            key: g.id,
            label: g.label,
            custom: Boolean(g.custom),
            items: (layout.items[g.id] || [])
                .map((path) => {
                    const meta = navCatalog.get(path);
                    if (!meta) return null;
                    return { path, title: meta.title, icon: meta.icon };
                })
                .filter(Boolean),
        }));
    }, [layout, navCatalog]);

    const stats = useMemo(() => {
        let total = 0;
        let hidden = 0;
        for (const g of customizerGroups) {
            for (const it of g.items) {
                total += 1;
                if (!isMenuPathVisible(it.path, hiddenPaths)) hidden += 1;
            }
        }
        return { total, hidden, visible: total - hidden };
    }, [customizerGroups, hiddenPaths]);

    const toggleGroupVisibility = (items, makeVisible) => {
        let next = hiddenPaths;
        for (const it of items) {
            if (MENU_ALWAYS_VISIBLE.has(it.path)) continue;
            next = setMenuPathVisible(next, it.path, makeVisible);
        }
        onHiddenChange(next);
    };

    const handleItemDrop = useCallback(
        (targetGroupId, targetIndex) => {
            const raw = dragOver?.path;
            if (!raw || !targetGroupId) return;
            onLayoutChange(moveMenuItem(layout, raw, targetGroupId, targetIndex));
            setDragOver(null);
        },
        [dragOver, layout, onLayoutChange]
    );

    const onItemDragStart = (e, path, groupId) => {
        e.dataTransfer.setData(DND_PATH, path);
        e.dataTransfer.setData("text/plain", path);
        e.dataTransfer.effectAllowed = "move";
        setDragOver({ type: "item", path, groupId });
    };

    const onGroupDragStart = (e, groupId, index) => {
        e.dataTransfer.setData(DND_GROUP, JSON.stringify({ groupId, index }));
        e.dataTransfer.effectAllowed = "move";
        setDragOver({ type: "group", groupId, index });
    };

    const onGroupDrop = (e, toIndex) => {
        e.preventDefault();
        try {
            const raw = e.dataTransfer.getData(DND_GROUP);
            if (!raw) return;
            const { index: fromIndex } = JSON.parse(raw);
            if (typeof fromIndex !== "number") return;
            onLayoutChange(reorderMenuGroups(layout, fromIndex, toIndex));
        } catch (_) {
            /* ignore */
        }
        setDragOver(null);
    };

    const startRename = (g) => {
        setRenameId(g.key);
        setRenameValue(g.label);
    };

    const commitRename = () => {
        if (renameId) {
            onLayoutChange(renameMenuGroup(layout, renameId, renameValue));
        }
        setRenameId(null);
        setRenameValue("");
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-lg bg-[#0c1222] border-[rgba(201,169,97,0.15)] text-slate-200 overflow-y-auto p-0"
            >
                <div className="p-6 pb-4 border-b border-white/5">
                    <SheetHeader>
                        <SheetTitle className="text-white flex items-center gap-2 font-display">
                            <Settings2 className="h-5 w-5 text-[var(--mbs-gold)]" />
                            Menüyü özelleştir
                        </SheetTitle>
                        <SheetDescription className="text-slate-500 text-sm">
                            Modülleri sürükleyip gruplara taşıyın, yeni grup oluşturun, görünürlüğü
                            açıp kapatın. Tercihler bu cihazda saklanır.
                        </SheetDescription>
                    </SheetHeader>

                    <div className="flex flex-wrap items-center gap-2 mt-4 text-xs">
                        <span className="text-slate-400 px-2 py-1 rounded bg-black/30 border border-white/10">
                            <span className="text-emerald-400 font-medium">{stats.visible}</span> görünür ·{" "}
                            {stats.hidden} gizli
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-[11px] border-white/10"
                            onClick={() => onHiddenChange(showAllMenuPaths())}
                        >
                            <Eye className="h-3 w-3 mr-1" />
                            Tümünü göster
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-[11px] border-white/10"
                            onClick={() => onLayoutChange(resetMenuLayout(catalogGroups))}
                        >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Düzeni sıfırla
                        </Button>
                    </div>
                </div>

                <div className="px-4 py-3 border-b border-white/5 bg-white/[0.02]">
                    <div className="flex gap-2">
                        <Input
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="Yeni grup adı (ör. Ekspertiz işlerim)"
                            className="h-9 bg-black/40 border-white/10 text-sm"
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && newGroupName.trim()) {
                                    onLayoutChange(addCustomMenuGroup(layout, newGroupName));
                                    setNewGroupName("");
                                }
                            }}
                        />
                        <Button
                            type="button"
                            size="sm"
                            className="h-9 shrink-0 bg-[var(--mbs-gold)]/20 text-[#F3E5AB] border border-[var(--mbs-gold)]/30 hover:bg-[var(--mbs-gold)]/30"
                            onClick={() => {
                                if (!newGroupName.trim()) return;
                                onLayoutChange(addCustomMenuGroup(layout, newGroupName));
                                setNewGroupName("");
                            }}
                        >
                            <FolderPlus className="h-4 w-4" />
                        </Button>
                    </div>
                    <p className="text-[10px] text-slate-600 mt-2 flex items-center gap-1">
                        <GripVertical className="h-3 w-3" />
                        Tutamaktan sürükleyin · gruba bırakın · grup başlığından sırayı değiştirin
                    </p>
                </div>

                <div className="p-4 space-y-4 pb-10">
                    {customizerGroups.map((group, groupIndex) => {
                        const items = group.items || [];
                        const groupHidden = items.filter(
                            (it) => !isMenuPathVisible(it.path, hiddenPaths)
                        ).length;
                        const allVisible = groupHidden === 0;
                        const allHidden =
                            items.filter((it) => !MENU_ALWAYS_VISIBLE.has(it.path)).length > 0 &&
                            groupHidden ===
                                items.filter((it) => !MENU_ALWAYS_VISIBLE.has(it.path)).length;

                        const isGroupDrop =
                            dragOver?.type === "group" && dragOver?.index === groupIndex;

                        return (
                            <div
                                key={group.key}
                                className={`rounded-lg border overflow-hidden transition-colors ${
                                    isGroupDrop
                                        ? "border-[var(--mbs-gold)]/50 bg-[var(--mbs-gold)]/5"
                                        : "border-white/8"
                                }`}
                                onDragOver={(e) => {
                                    if (e.dataTransfer.types.includes(DND_GROUP)) {
                                        e.preventDefault();
                                    }
                                }}
                                onDrop={(e) => onGroupDrop(e, groupIndex)}
                            >
                                <div
                                    draggable
                                    onDragStart={(e) => onGroupDragStart(e, group.key, groupIndex)}
                                    className="flex items-center gap-2 px-3 py-2 bg-white/[0.04] border-b border-white/5 cursor-grab active:cursor-grabbing"
                                >
                                    <GripVertical className="h-4 w-4 text-slate-600 shrink-0" />
                                    {renameId === group.key ? (
                                        <Input
                                            autoFocus
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onBlur={commitRename}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") commitRename();
                                                if (e.key === "Escape") setRenameId(null);
                                            }}
                                            className="h-7 text-xs bg-black/50 border-white/10 flex-1"
                                        />
                                    ) : (
                                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 flex-1 truncate">
                                            {group.label}
                                        </span>
                                    )}
                                    {group.custom ? (
                                        <div className="flex items-center gap-0.5 shrink-0">
                                            <button
                                                type="button"
                                                className="p-1 text-slate-500 hover:text-slate-300"
                                                onClick={() => startRename(group)}
                                                title="Yeniden adlandır"
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </button>
                                            <button
                                                type="button"
                                                className="p-1 text-slate-500 hover:text-rose-400"
                                                onClick={() =>
                                                    onLayoutChange(
                                                        removeCustomMenuGroup(layout, group.key)
                                                    )
                                                }
                                                title="Grubu sil"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ) : null}
                                    <div className="flex gap-1 shrink-0 text-[10px]">
                                        <button
                                            type="button"
                                            className="text-slate-500 hover:text-emerald-400"
                                            onClick={() => toggleGroupVisibility(items, true)}
                                            disabled={allVisible}
                                        >
                                            Aç
                                        </button>
                                        <span className="text-slate-700">|</span>
                                        <button
                                            type="button"
                                            className="text-slate-500 hover:text-rose-400"
                                            onClick={() => toggleGroupVisibility(items, false)}
                                            disabled={allHidden}
                                        >
                                            Kapat
                                        </button>
                                    </div>
                                </div>

                                <ul
                                    className="min-h-[2rem] divide-y divide-white/5"
                                    onDragOver={(e) => {
                                        if (e.dataTransfer.types.includes(DND_PATH)) {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = "move";
                                        }
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        handleItemDrop(group.key, items.length);
                                    }}
                                >
                                    {items.length === 0 ? (
                                        <li className="px-3 py-4 text-center text-[11px] text-slate-600 border border-dashed border-white/5 m-2 rounded-md">
                                            Buraya sürükleyin
                                        </li>
                                    ) : (
                                        items.map((it, itemIndex) => {
                                            const locked = MENU_ALWAYS_VISIBLE.has(it.path);
                                            const visible = isMenuPathVisible(it.path, hiddenPaths);
                                            const dropBefore =
                                                dragOver?.type === "item" &&
                                                dragOver?.groupId === group.key &&
                                                dragOver?.index === itemIndex;

                                            return (
                                                <li
                                                    key={it.path}
                                                    draggable={!locked}
                                                    onDragStart={(e) =>
                                                        onItemDragStart(e, it.path, group.key)
                                                    }
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const rect =
                                                            e.currentTarget.getBoundingClientRect();
                                                        const before =
                                                            e.clientY <
                                                            rect.top + rect.height / 2;
                                                        setDragOver({
                                                            type: "item",
                                                            path: it.path,
                                                            groupId: group.key,
                                                            index: before
                                                                ? itemIndex
                                                                : itemIndex + 1,
                                                        });
                                                    }}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const rect =
                                                            e.currentTarget.getBoundingClientRect();
                                                        const before =
                                                            e.clientY <
                                                            rect.top + rect.height / 2;
                                                        const idx = before
                                                            ? itemIndex
                                                            : itemIndex + 1;
                                                        const dragged =
                                                            e.dataTransfer.getData(DND_PATH) ||
                                                            e.dataTransfer.getData("text/plain");
                                                        if (dragged) {
                                                            onLayoutChange(
                                                                moveMenuItem(
                                                                    layout,
                                                                    dragged,
                                                                    group.key,
                                                                    idx
                                                                )
                                                            );
                                                        }
                                                        setDragOver(null);
                                                    }}
                                                    className={`flex items-center gap-2 px-2 py-2 hover:bg-white/[0.03] ${
                                                        dropBefore
                                                            ? "bg-[var(--mbs-gold)]/10 border-t-2 border-[var(--mbs-gold)]"
                                                            : ""
                                                    } ${locked ? "" : "cursor-grab active:cursor-grabbing"}`}
                                                >
                                                    <GripVertical
                                                        className={`h-4 w-4 shrink-0 ${
                                                            locked
                                                                ? "text-slate-800"
                                                                : "text-slate-600"
                                                        }`}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm text-slate-200 truncate">
                                                            {it.title}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {visible ? (
                                                            <Eye className="h-3.5 w-3.5 text-emerald-500/70" />
                                                        ) : (
                                                            <EyeOff className="h-3.5 w-3.5 text-slate-600" />
                                                        )}
                                                        <Switch
                                                            checked={visible}
                                                            disabled={locked}
                                                            onCheckedChange={(checked) =>
                                                                onHiddenChange(
                                                                    setMenuPathVisible(
                                                                        hiddenPaths,
                                                                        it.path,
                                                                        checked
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                </li>
                                            );
                                        })
                                    )}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </SheetContent>
        </Sheet>
    );
}
